import mapboxgl from "mapbox-gl";
import type { Feature, FeatureCollection, Point } from "geojson";
import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import type { StageConnector } from "../../../services/stage-connectors";

/**
 * Joins between consecutive routes of the same day (detectRouteJoins): a
 * small white handle with a yellow ring — smaller than a day connector, with
 * no number, since it isn't a stage end. Dragging it moves both routes' shared
 * endpoint, using the same connector preview and finalize as a day connector
 * (where finalizeConnectorDrag moves it like any point on the line — only a
 * flag between two days slides along it).
 */

export const ROUTE_JOIN_SOURCE = 'route-join-handles';
export const ROUTE_JOIN_LAYER = 'route-join-handle-layer';

export function buildRouteJoinData(joins: StageConnector[]): FeatureCollection {
  const features: Feature<Point>[] = joins.map(join => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [join.position[0], join.position[1]] },
    properties: { joinId: join.id, prevRouteKey: join.prevRouteKey, nextRouteKey: join.nextRouteKey },
  }));
  return { type: 'FeatureCollection', features };
}

let joinData: FeatureCollection | null = null;
let listeners: Array<[string, any]> = [];

function detach(map: mapboxgl.Map) {
  listeners.forEach(([type, fn]) => map.off(type as any, ROUTE_JOIN_LAYER, fn));
  listeners = [];
}

export const routeJoinActions = {
  async updateRouteJoinsOnMap({ state, getters, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;
    const layerRegistry = getLayerRegistry();
    const joins: StageConnector[] = getters.routeJoins || [];

    if (!joins.length) {
      detach(map);
      safeRemoveLayer(ROUTE_JOIN_LAYER);
      if (map.getSource(ROUTE_JOIN_SOURCE)) {
        try { map.removeSource(ROUTE_JOIN_SOURCE); } catch (_) {}
      }
      joinData = null;
      return;
    }

    joinData = buildRouteJoinData(joins);
    const source = map.getSource(ROUTE_JOIN_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (source && map.getLayer(ROUTE_JOIN_LAYER)) {
      try { source.setData(joinData as any); } catch (_) {}
      if (listeners.length) return;
    } else {
      layerRegistry.addSource(ROUTE_JOIN_SOURCE, { type: 'geojson', data: joinData }, true);
      const layer: mapboxgl.CircleLayer = {
        id: ROUTE_JOIN_LAYER,
        type: 'circle',
        source: ROUTE_JOIN_SOURCE,
        paint: {
          'circle-radius': 6,
          'circle-color': '#ffffff',
          'circle-stroke-color': '#fddf00',
          'circle-stroke-width': 2.5,
          'circle-pitch-alignment': 'map',
        },
      };
      // Just under the day connectors (Top, z 20) — a day join wins any overlap.
      layerRegistry.addLayer(layer, MapLayerCategory.Top, { z: 19 });
    }

    detach(map);
    const canvas = map.getCanvas();
    let dragging: { joinId: string } | null = null;
    let lastLngLat: mapboxgl.LngLat | null = null;
    const moveHandle = (lngLat: mapboxgl.LngLat) => {
      const feature = joinData?.features.find(f => f.properties?.joinId === dragging?.joinId);
      if (!feature) return;
      (feature.geometry as Point).coordinates = [lngLat.lng, lngLat.lat];
      const src = map.getSource(ROUTE_JOIN_SOURCE) as mapboxgl.GeoJSONSource;
      if (src) { try { src.setData(joinData as any); } catch (_) {} }
    };

    const onMove = (e: mapboxgl.MapMouseEvent) => {
      if (!dragging) return;
      lastLngLat = e.lngLat;
      moveHandle(e.lngLat);
      canvas.style.cursor = 'grabbing';
      (dispatch as any)('updateConnectorDragPreview', [e.lngLat.lng, e.lngLat.lat]);
    };

    const onUp = async () => {
      map.off('mousemove', onMove);
      map.off('touchmove', onMove as any);
      if (!dragging) return;
      dragging = null;
      const moved = lastLngLat;
      lastLngLat = null;
      canvas.style.cursor = 'grab';
      if (!moved) {
        await (dispatch as any)('cancelDragPreview');
        await (dispatch as any)('updateRouteJoinsOnMap');
        return;
      }
      const applied = await (dispatch as any)('finalizeConnectorDrag', { fallbackPosition: [moved.lng, moved.lat] });
      if (!applied) await (dispatch as any)('updateRouteJoinsOnMap');
    };

    // Same gates a day connector drag goes through.
    const allowed = (feature: any): boolean => {
      const win = window as any;
      const referenced = [state.routes[feature.properties?.prevRouteKey], state.routes[feature.properties?.nextRouteKey]]
        .filter(route => route && route.isReference);
      if (referenced.length && typeof win.canEditReference === 'function' &&
          referenced.some(route => !win.canEditReference(route))) {
        win.checkReferenceEditWarning?.(referenced[0]);
        return false;
      }
      return true;
    };

    const start = (e: any, moveEvent: 'mousemove' | 'touchmove', upEvent: 'mouseup' | 'touchend') => {
      const feature = e.features && e.features[0];
      if (!feature || !allowed(feature)) return;
      e.preventDefault();
      dragging = { joinId: feature.properties.joinId };
      lastLngLat = null;
      (dispatch as any)('initializeConnectorDrag', {
        prevRouteKey: feature.properties.prevRouteKey,
        nextRouteKey: feature.properties.nextRouteKey,
        position: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]],
      });
      map.on(moveEvent, onMove as any);
      map.once(upEvent, onUp as any);
    };

    const mouseDown = (e: any) => {
      if (e.originalEvent && e.originalEvent.button !== 0) return;
      start(e, 'mousemove', 'mouseup');
    };
    const touchStart = (e: any) => {
      if (!e.points || e.points.length !== 1) return;
      start(e, 'touchmove', 'touchend');
    };
    const hover = () => { if (!dragging) canvas.style.cursor = 'grab'; };
    const leave = () => { if (!dragging) canvas.style.cursor = 'crosshair'; };

    listeners = [['mouseenter', hover], ['mousemove', hover], ['mouseleave', leave], ['mousedown', mouseDown], ['touchstart', touchStart]];
    listeners.forEach(([type, fn]) => map.on(type as any, ROUTE_JOIN_LAYER, fn));
  },
};
